// Active-filter descriptors – turns a DeviceFiltersValue into a flat list of the
// non-default filters that are currently applied. Used to count active filters
// for the toolbar "Filters" card.

import type { DeviceFiltersValue } from '../../../../types/DeviceListTypes';

interface ActiveFilterChip {
  id: string;
  label: string;
  patch: Partial<DeviceFiltersValue>;
}

const LIFECYCLE_LABELS: Record<string, string> = {
  active: 'In service',
  lost: 'Lost',
  decommissioned: 'Decommissioned',
  damaged: 'Damaged',
  errored: 'Errored',
};

const LAST_CMD_TYPE_LABELS: Record<string, string> = {
  REAR_FW_DWNLD: 'Firmware download (rear)',
  FRONT_FW_DWNLD: 'Firmware download (front)',
  REAR_LOG_UPLD: 'Log upload (rear)',
  FRONT_LOG_UPLD: 'Log upload (front)',
  REAR_DELETE_LOGS: 'Delete logs (rear)',
  FRONT_DELETE_LOGS: 'Delete logs (front)',
  REAR_TOGGLE_LED: 'Toggle LED (rear)',
  FRONT_TOGGLE_LED: 'Toggle LED (front)',
  REAR_APP_MODE: 'App mode',
  REAR_RACE_MODE: 'Race mode',
  REAR_SLEEP: 'Sleep',
  REAR_REBOOT: 'Reboot',
  REAR_WINTER: 'Winter mode',
  REAR_MODEM_DFOTA: 'Modem DFOTA',
};

/** datetime-local strings look like 2026-06-30T15:30 – make them readable. */
function fmtDateTime(value: string): string {
  return value.replace('T', ' ');
}

/** Build a chip descriptor for every non-default filter field. */
export function getActiveFilterChips(f: DeviceFiltersValue): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (f.online !== 'any')
    chips.push({ id: 'online', label: f.online === 'online' ? 'Online' : 'Offline', patch: { online: 'any' } });
  if (f.tls !== 'any')
    chips.push({
      id: 'tls',
      label: f.tls === 'provisioned' ? 'TLS provisioned' : 'TLS unprovisioned',
      patch: { tls: 'any' },
    });
  if (f.lifecycle !== 'any')
    chips.push({ id: 'lifecycle', label: LIFECYCLE_LABELS[f.lifecycle] ?? f.lifecycle, patch: { lifecycle: 'any' } });
  if (f.mode !== 'any') chips.push({ id: 'mode', label: `Mode: ${f.mode}`, patch: { mode: 'any' } });
  if (f.deviceRole !== 'any')
    chips.push({
      id: 'deviceRole',
      label: f.deviceRole === 'rear' ? 'Rear only' : 'Front only',
      patch: { deviceRole: 'any' },
    });
  if (f.frontLink !== 'any')
    chips.push({
      id: 'frontLink',
      label: f.frontLink === 'linked' ? 'Front linked' : 'Front unlinked',
      patch: { frontLink: 'any' },
    });

  if (f.battMvMin != null) chips.push({ id: 'battMvMin', label: `Batt ≥ ${f.battMvMin} mV`, patch: { battMvMin: null } });
  if (f.battMvMax != null) chips.push({ id: 'battMvMax', label: `Batt ≤ ${f.battMvMax} mV`, patch: { battMvMax: null } });

  if (f.raceLogMin != null)
    chips.push({ id: 'raceLogMin', label: `Race log ≥ ${f.raceLogMin} B`, patch: { raceLogMin: null } });
  if (f.raceLogMax != null)
    chips.push({ id: 'raceLogMax', label: `Race log ≤ ${f.raceLogMax} B`, patch: { raceLogMax: null } });

  if (f.debugLogMin != null)
    chips.push({ id: 'debugLogMin', label: `Debug log ≥ ${f.debugLogMin} B`, patch: { debugLogMin: null } });
  if (f.debugLogMax != null)
    chips.push({ id: 'debugLogMax', label: `Debug log ≤ ${f.debugLogMax} B`, patch: { debugLogMax: null } });

  if (f.lastHeardFrom)
    chips.push({ id: 'lastHeardFrom', label: `Heard ≥ ${fmtDateTime(f.lastHeardFrom)}`, patch: { lastHeardFrom: '' } });
  if (f.lastHeardTo)
    chips.push({ id: 'lastHeardTo', label: `Heard ≤ ${fmtDateTime(f.lastHeardTo)}`, patch: { lastHeardTo: '' } });

  if (f.lastCmdType !== 'any')
    chips.push({
      id: 'lastCmdType',
      label: `Cmd: ${LAST_CMD_TYPE_LABELS[f.lastCmdType] ?? f.lastCmdType}`,
      patch: { lastCmdType: 'any' },
    });
  if (f.lastCmdStatuses && f.lastCmdStatuses.length > 0)
    chips.push({
      id: 'lastCmdStatuses',
      label: `Cmd status: ${f.lastCmdStatuses.join(', ')}`,
      patch: { lastCmdStatuses: [] },
    });
  if ((f.lastCmdTargetContains ?? '').trim().length > 0)
    chips.push({
      id: 'lastCmdTargetContains',
      label: `Target ~ "${f.lastCmdTargetContains.trim()}"`,
      patch: { lastCmdTargetContains: '' },
    });
  if (f.lastCmdUpdatedFrom)
    chips.push({
      id: 'lastCmdUpdatedFrom',
      label: `Cmd upd ≥ ${fmtDateTime(f.lastCmdUpdatedFrom)}`,
      patch: { lastCmdUpdatedFrom: '' },
    });
  if (f.lastCmdUpdatedTo)
    chips.push({
      id: 'lastCmdUpdatedTo',
      label: `Cmd upd ≤ ${fmtDateTime(f.lastCmdUpdatedTo)}`,
      patch: { lastCmdUpdatedTo: '' },
    });

  if (f.locationCenterLat != null && f.locationCenterLng != null && f.locationRadiusKm != null)
    chips.push({
      id: 'location',
      label: `Within ${f.locationRadiusKm} km`,
      patch: { locationCenterLat: null, locationCenterLng: null, locationRadiusKm: null },
    });

  return chips;
}
